import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BarChart2, Send, Lock, X, Search, Loader2 } from "lucide-react";
import { API_BASE_URL } from "../../../config";
import {
  MAX_FIXTURE_TAGS,
  detectMentionQuery,
  isTaggingActive,
  isFixtureLiveStatus,
  removeMentionToken,
  roomToFixtureRef,
  fixtureRefId,
} from "../../utils/bondchat/fixtureTagHelpers";
import FixtureTagChips from "./FixtureTagChips";
import ReplyPreviewBar from "./ReplyPreviewBar";
import ChallengeDropSheet from "./ChallengeDropSheet";

const FIXTURE_GROUPS = [
  { key: "live", label: "Live now" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "later", label: "Later" },
];

const sameLocalDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const fixtureGroup = (room) => {
  const ref = roomToFixtureRef(room);
  if (room.isLive || room.tagBucket === "live" || isFixtureLiveStatus(ref)) return "live";
  if (!ref.timestamp) return "later";
  const kickoff = new Date(Number(ref.timestamp) * 1000);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (sameLocalDay(kickoff, today)) return "today";
  if (sameLocalDay(kickoff, tomorrow)) return "tomorrow";
  return "later";
};

const kickoffLabel = (ref) => {
  if (!ref?.timestamp) return "Upcoming";
  const kickoff = new Date(Number(ref.timestamp) * 1000);
  const options = sameLocalDay(kickoff, new Date())
    ? { hour: "2-digit", minute: "2-digit" }
    : { weekday: "short", hour: "2-digit", minute: "2-digit" };
  return new Intl.DateTimeFormat(undefined, options).format(kickoff);
};

const FixtureSearchRow = ({ room, onSelect, challengeMode }) => {
  const ref = roomToFixtureRef(room);
  const live = isFixtureLiveStatus(ref) || room.isLive;
  const status = live
    ? ref?.status?.elapsed ? `${ref.status.elapsed}'` : "LIVE"
    : kickoffLabel(ref);
  return (
    <button
      key={fixtureRefId(ref)}
      type="button"
      className="bc-tag-result"
      onClick={() => onSelect(room)}
    >
      <span className="bc-tag-result-meta">
        {room.league?.logo && (
          <img src={room.league.logo} alt="" className="bc-tag-result-league-logo" />
        )}
        <span className="bc-tag-result-league">{room.league?.name || "League"}</span>
        <span className={`bc-tag-result-badge ${live ? "bc-tag-result-badge-live" : "bc-tag-result-badge-upcoming"}`}>
          {live && <span className="bc-ftag-live-dot" />}
          {status}
        </span>
      </span>
      <span className="bc-tag-result-body">
        <span className="bc-tag-result-teams">
          <span className="bc-tag-result-team">
            {room.homeTeam?.logo && <img src={room.homeTeam.logo} alt="" className="bc-tag-result-logo" />}
            <span className="bc-tag-result-name">{room.homeTeam?.name || "Home"}</span>
            {live && <strong>{ref.goals?.home ?? "–"}</strong>}
          </span>
          <span className="bc-tag-result-team">
            {room.awayTeam?.logo && <img src={room.awayTeam.logo} alt="" className="bc-tag-result-logo" />}
            <span className="bc-tag-result-name">{room.awayTeam?.name || "Away"}</span>
            {live && <strong>{ref.goals?.away ?? "–"}</strong>}
          </span>
        </span>
        <span className="bc-tag-result-select">{challengeMode ? "Challenge" : "Add"} ›</span>
      </span>
    </button>
  );
};

const FixtureTagSearchPanel = ({
  search,
  onSearchChange,
  onClose,
  loading,
  results,
  onSelect,
  challengeMode = false,
}) => {
  const grouped = FIXTURE_GROUPS.map((group) => ({
    ...group,
    items: results
      .filter((room) => fixtureGroup(room) === group.key)
      .sort((a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0)),
  })).filter((group) => group.items.length);

  return (
    <div className="bc-tag-panel">
      <div className="bc-tag-panel-intro">
        <span>
          <strong>{challengeMode ? "Challenge a match" : "Find a match"}</strong>
          <small>{challengeMode ? "Select one fixture to challenge" : "Live and upcoming fixtures"}</small>
        </span>
        <button type="button" className="bc-tag-close" onClick={onClose} aria-label="Close fixture picker">
          <X size={14} />
        </button>
      </div>
      <div className="bc-tag-panel-head">
        <Search size={14} />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search team or league…"
          className="bc-tag-search"
        />
      </div>
      <div className="bc-tag-results">
        {loading && <Loader2 size={14} className="animate-spin bc-tag-loader" />}
        {!loading && results.length === 0 && (
          <p className="bc-tag-empty">
            {search.trim() ? `No matches found for “${search.trim()}”` : "Live and upcoming matches will appear here"}
          </p>
        )}
        {!loading && grouped.map((group) => (
          <section key={group.key} className="bc-tag-section">
            <header className={`bc-tag-section-head${group.key === "live" ? " is-live" : ""}`}>
              <span>{group.label}</span>
              <small>{group.items.length}</small>
            </header>
            {group.items.map((room) => (
              <FixtureSearchRow
                key={fixtureRefId(roomToFixtureRef(room))}
                room={room}
                onSelect={onSelect}
                challengeMode={challengeMode}
              />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
};

const ChatComposer = ({
  disabled = false,
  disabledHint = "Chat is read-only",
  enableFixtureTags = false,
  enableChatChallenges = false,
  replyTarget = null,
  onCancelReply,
  onTyping,
  onSendMessage,
  onCreateChallenge,
  onCreatePoll,
  reportedMessageId,
  onClearReportTarget,
  onSubmitReport,
}) => {
  const [text, setText] = useState("");
  const [selectedFixtures, setSelectedFixtures] = useState([]);
  const [pollOpen, setPollOpen] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollFixtures, setPollFixtures] = useState([]);
  const [reportReason, setReportReason] = useState("");
  const [tagging, setTagging] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [tagResults, setTagResults] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [pollTagging, setPollTagging] = useState(false);
  const [pollTagSearch, setPollTagSearch] = useState("");
  const [pollTagResults, setPollTagResults] = useState([]);
  const [pollTagLoading, setPollTagLoading] = useState(false);
  const [challengeRoom, setChallengeRoom] = useState(null);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const searchTimer = useRef(null);
  const typingStateRef = useRef({ isTyping: false, timer: null });

  const stopTyping = useCallback(() => {
    if (typingStateRef.current.timer) {
      clearTimeout(typingStateRef.current.timer);
      typingStateRef.current.timer = null;
    }
    if (typingStateRef.current.isTyping) {
      typingStateRef.current.isTyping = false;
      onTyping?.(false);
    }
  }, [onTyping]);

  const signalTyping = useCallback(() => {
    if (!onTyping) return;
    if (!typingStateRef.current.isTyping) {
      typingStateRef.current.isTyping = true;
      onTyping(true);
    }
    if (typingStateRef.current.timer) clearTimeout(typingStateRef.current.timer);
    typingStateRef.current.timer = setTimeout(() => {
      typingStateRef.current.timer = null;
      typingStateRef.current.isTyping = false;
      onTyping(false);
    }, 2500);
  }, [onTyping]);

  useEffect(() => () => stopTyping(), [stopTyping]);

  const fetchTagResults = useCallback(async (q, setResults, setLoading) => {
    setLoading(true);
    try {
      const trimmed = (q || "").trim();
      const query = `limit=12${trimmed ? `&q=${encodeURIComponent(trimmed)}` : ""}`;
      const res = await fetch(`${API_BASE_URL}/api/general-chat/fixtures/tag-search?${query}`);
      const raw = await res.text();
      let json = null;
      try {
        json = raw ? JSON.parse(raw) : null;
      } catch {
        setResults([]);
        return;
      }
      if (!res.ok) {
        setResults([]);
        return;
      }
      setResults(Array.isArray(json?.data) ? json.data : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const scheduleTagSearch = useCallback(
    (q, setResults, setLoading) => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => fetchTagResults(q, setResults, setLoading), 220);
    },
    [fetchTagResults]
  );

  useEffect(() => {
    if (!enableFixtureTags || !tagging) return undefined;
    scheduleTagSearch(tagSearch, setTagResults, setTagLoading);
    return () => clearTimeout(searchTimer.current);
  }, [enableFixtureTags, tagging, tagSearch, scheduleTagSearch]);

  useEffect(() => {
    if (!enableFixtureTags || !pollTagging) return undefined;
    scheduleTagSearch(pollTagSearch, setPollTagResults, setPollTagLoading);
    return () => clearTimeout(searchTimer.current);
  }, [enableFixtureTags, pollTagging, pollTagSearch, scheduleTagSearch]);

  const handleChange = (value) => {
    setText(value);
    if (value.trim()) signalTyping();
    else stopTyping();
    if (!enableFixtureTags) {
      setTagging(false);
      return;
    }
    const active = isTaggingActive(value);
    setTagging(active);
    setTagSearch(active ? detectMentionQuery(value)?.query || "" : "");
  };

  const handlePollQuestionChange = (value) => {
    setPollQuestion(value);
    if (!enableFixtureTags) {
      setPollTagging(false);
      return;
    }
    const active = isTaggingActive(value);
    setPollTagging(active);
    setPollTagSearch(active ? detectMentionQuery(value)?.query || "" : "");
  };

  const addFixture = (room, target = "message") => {
    const ref = roomToFixtureRef(room);
    const id = fixtureRefId(ref);
    if (!id) return;
    const setter = target === "poll" ? setPollFixtures : setSelectedFixtures;
    setter((prev) => {
      if (prev.some((r) => fixtureRefId(r) === id)) return prev;
      if (prev.length >= MAX_FIXTURE_TAGS) return prev;
      return [...prev, ref];
    });
    if (target === "message") {
      setText(removeMentionToken(text).text);
      setTagging(false);
      setTagSearch("");
      setTagResults([]);
    } else {
      setPollQuestion(removeMentionToken(pollQuestion).text);
      setPollTagging(false);
      setPollTagSearch("");
      setPollTagResults([]);
    }
  };

  const pickMessageFixture = (room) => {
    if (enableChatChallenges) {
      setText(removeMentionToken(text).text);
      setTagging(false);
      setTagSearch("");
      setTagResults([]);
      setChallengeRoom(room);
      setChallengeOpen(true);
      return;
    }
    addFixture(room, "message");
  };

  const closeMessageTagPanel = () => {
    setTagging(false);
    setTagSearch("");
    setTagResults([]);
    setText(removeMentionToken(text).text);
  };

  const closePollTagPanel = () => {
    setPollTagging(false);
    setPollTagSearch("");
    setPollTagResults([]);
    setPollQuestion(removeMentionToken(pollQuestion).text);
  };

  const openPollModal = () => {
    setPollQuestion("");
    setPollFixtures([]);
    setPollTagging(false);
    setPollTagSearch("");
    setPollTagResults([]);
    setPollOpen(true);
  };

  const closePollModal = () => {
    setPollOpen(false);
    setPollTagging(false);
    setPollTagSearch("");
    setPollTagResults([]);
  };

  const send = () => {
    const trimmed = text.trim();
    if (disabled || !trimmed) return;
    stopTyping();
    onSendMessage?.(trimmed, enableFixtureTags && !enableChatChallenges ? selectedFixtures : [], replyTarget);
    setText("");
    setSelectedFixtures([]);
    setTagging(false);
    onCancelReply?.();
  };

  const submitPoll = () => {
    const q = pollQuestion.trim();
    if (disabled || !q) return;
    onCreatePoll?.(q, enableFixtureTags ? pollFixtures : []);
    setPollQuestion("");
    setPollFixtures([]);
    setPollTagging(false);
    setPollTagSearch("");
    setPollTagResults([]);
    setPollOpen(false);
  };

  if (disabled) {
    return (
      <div className="bc-composer bc-composer-locked">
        <Lock size={15} />
        <span>{disabledHint}</span>
      </div>
    );
  }

  return (
    <>
      <div className="bc-composer bc-pulse-composer">
        <ReplyPreviewBar replyTarget={replyTarget} onCancel={onCancelReply} />
        {enableFixtureTags && !enableChatChallenges && selectedFixtures.length > 0 && (
          <FixtureTagChips
            refs={selectedFixtures}
            variant="composer"
            onRemove={(ref) => {
              const id = fixtureRefId(ref);
              setSelectedFixtures((prev) => prev.filter((r) => fixtureRefId(r) !== id));
            }}
          />
        )}
        {tagging && (
          <FixtureTagSearchPanel
            search={tagSearch}
            onSearchChange={setTagSearch}
            onClose={closeMessageTagPanel}
            loading={tagLoading}
            results={tagResults}
            onSelect={pickMessageFixture}
            challengeMode={enableChatChallenges}
          />
        )}
        <div className="bc-composer-row">
          <button type="button" className="bc-composer-tool" onClick={openPollModal} aria-label="Create poll">
            <BarChart2 size={16} />
          </button>
          <textarea
            className="bc-composer-input"
            value={text}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={replyTarget ? "Write your reply…" : enableChatChallenges ? "Message… @ to drop a challenge" : enableFixtureTags ? "Message… @ to tag a match" : "Message…"}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <button
            type="button"
            className={`bc-composer-send ${text.trim() ? "bc-composer-send-active" : ""}`}
            disabled={!text.trim()}
            onClick={send}
            aria-label="Post"
          >
            <Send size={15} />
          </button>
        </div>
      </div>

      {pollOpen && createPortal(
        <div className="bc-modal-root" role="presentation">
          <button type="button" className="bc-modal-backdrop" aria-label="Close" onClick={closePollModal} />
          <div className="bc-modal-sheet" role="dialog" aria-modal="true">
            <h3 className="bc-modal-title">Create poll</h3>
            {enableFixtureTags && pollFixtures.length > 0 && (
              <FixtureTagChips
                refs={pollFixtures}
                variant="composer"
                onRemove={(ref) => {
                  const id = fixtureRefId(ref);
                  setPollFixtures((prev) => prev.filter((r) => fixtureRefId(r) !== id));
                }}
              />
            )}
            {enableFixtureTags && pollTagging && (
              <FixtureTagSearchPanel
                search={pollTagSearch}
                onSearchChange={setPollTagSearch}
                onClose={closePollTagPanel}
                loading={pollTagLoading}
                results={pollTagResults}
                onSelect={(room) => addFixture(room, "poll")}
              />
            )}
            <textarea
              className="bc-composer-input bc-modal-input"
              value={pollQuestion}
              onChange={(e) => handlePollQuestionChange(e.target.value)}
              placeholder={enableFixtureTags ? "Ask yes or no… use @ to tag a match" : "Ask yes or no…"}
              rows={3}
            />
            {enableFixtureTags && (
              <p className="bc-poll-tag-hint">Type @ to tag a live or upcoming match</p>
            )}
            <div className="bc-modal-actions">
              <button type="button" className="bc-modal-btn-ghost" onClick={closePollModal}>Cancel</button>
              <button type="button" className="bc-modal-btn-primary" disabled={!pollQuestion.trim()} onClick={submitPoll}>
                Post poll
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {reportedMessageId && createPortal(
        <div className="bc-modal-root" role="presentation">
          <button type="button" className="bc-modal-backdrop" aria-label="Close" onClick={onClearReportTarget} />
          <div className="bc-modal-sheet" role="dialog" aria-modal="true">
            <h3 className="bc-modal-title">Report message</h3>
            <textarea
              className="bc-composer-input bc-modal-input"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
            />
            <div className="bc-modal-actions">
              <button type="button" className="bc-modal-btn-ghost" onClick={onClearReportTarget}>Cancel</button>
              <button
                type="button"
                className="bc-modal-btn-danger"
                onClick={() => {
                  onSubmitReport?.(reportedMessageId, reportReason.trim());
                  setReportReason("");
                }}
              >
                Report
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ChallengeDropSheet
        open={challengeOpen}
        fixtureRoom={challengeRoom}
        onClose={() => {
          setChallengeOpen(false);
          setChallengeRoom(null);
        }}
        onDrop={onCreateChallenge}
      />
    </>
  );
};

export default ChatComposer;
